import React from 'react';
import { Input, Tag } from 'antd';
import { dragRankOrderMatches, uniqueId } from '../cardTypes';
import { rankedFields, rankIndexes, RankItem } from '../dragRank';
import { AddButton, isBlank, ItemActions, RichTextArea, Section } from '../fields';
import { asArray, moveItem, removeAt, replaceAt } from '../listOps';
import { CardEditorProps } from './types';

/**
 * Edits the items in their correct order. `correctOrder` is rewritten from this list on
 * every edit; the stored `items` order is kept apart from it (see dragRank.ts).
 */
const DragRankEditor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const items = asArray<RankItem>(card.items);
  const ranking = rankIndexes(items, asArray<string>(card.correctOrder));

  const write = (nextItems: RankItem[], nextRanking: number[]) =>
    onChange({ ...card, ...rankedFields(nextItems, nextRanking) });
  const patch = (position: number, fields: Partial<RankItem>) => {
    const index = ranking[position];
    write(replaceAt(items, index, { ...items[index], ...fields }), ranking);
  };
  const remove = (position: number) => {
    const index = ranking[position];
    write(removeAt(items, index), removeAt(ranking, position).map((i) => (i > index ? i - 1 : i)));
  };
  const add = () => {
    const id = uniqueId(items.map((it) => it.id), 'i');
    write([...items, { id, text: '', icon: '' }], [...ranking, items.length]);
  };

  return (
    <Section
      title="Items — in the correct order"
      help="The app shuffles them; the user has to restore this order."
      extra={!dragRankOrderMatches(card) && <Tag color="warning">Order out of sync — any edit fixes it</Tag>}
    >
      <div className="lbe-items">
        {ranking.map((index, position) => {
          const item = items[index];
          return (
            <div key={position} className="lbe-list-row">
              <span className="lbe-letter" style={{ marginTop: 5 }}>{position + 1}</span>
              <Input
                value={item.icon ?? ''}
                placeholder="🙂"
                style={{ width: 64, flex: 'none', textAlign: 'center' }}
                onChange={(e) => patch(position, { icon: e.target.value })}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <RichTextArea
                  minRows={1}
                  value={item.text}
                  status={isBlank(item.text) ? 'warning' : undefined}
                  onChange={(text) => patch(position, { text })}
                />
              </div>
              <ItemActions
                index={position}
                count={ranking.length}
                onMove={(to) => write(items, moveItem(ranking, position, to))}
                onRemove={() => remove(position)}
              />
            </div>
          );
        })}
        <AddButton onClick={add}>Add item</AddButton>
      </div>
    </Section>
  );
};

export default DragRankEditor;
